import React, { useCallback, useEffect, useState } from 'react';
import { Bell, Loader2, Save } from 'lucide-react';
import api from '../api';
import { useSuccessFailure } from '../context/SuccessFailureContext';

/**
 * Per-category notification channel preferences (in-app / email / SMS).
 * Mirrors GET/PUT /api/notifications/preferences/.
 */
const CHANNELS = [
  { key: 'in_app', label: 'In-app' },
  { key: 'email', label: 'Email' },
  { key: 'sms', label: 'SMS' },
];

const NotificationSettings = () => {
  const { showSuccessModal, showFailureModal } = useSuccessFailure();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/notifications/preferences/');
      setRows(res.data || []);
    } catch (e) {
      showFailureModal('Error', 'Could not load notification settings.');
    } finally {
      setLoading(false);
    }
  }, [showFailureModal]);

  useEffect(() => { load(); }, [load]);

  const toggle = (category, channel) => {
    setRows((prev) => prev.map((r) =>
      r.category === category ? { ...r, [channel]: !r[channel] } : r
    ));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = rows.map(({ category, in_app, email, sms }) => ({ category, in_app, email, sms }));
      const res = await api.put('/notifications/preferences/', payload);
      setRows(res.data || rows);
      showSuccessModal('Saved', 'Your notification preferences were updated.');
    } catch (e) {
      showFailureModal('Save failed', 'Could not save notification settings.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg bg-white mb-8">
      <div className="p-6">
        <div className="flex items-center mb-1">
          <Bell className="w-5 h-5 text-blue-600 mr-2" />
          <h2 className="text-xl font-semibold text-gray-900">Notifications</h2>
        </div>
        <p className="text-sm text-gray-500 mb-4">Choose how you want to be notified for each kind of update.</p>

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-7 w-7 animate-spin text-blue-600" /></div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase text-gray-500">
                    <th className="text-left py-2 pr-4">Type</th>
                    {CHANNELS.map((c) => (
                      <th key={c.key} className="text-center py-2 px-3">{c.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((row) => (
                    <tr key={row.category}>
                      <td className="py-3 pr-4 text-gray-800">{row.label}</td>
                      {CHANNELS.map((c) => (
                        <td key={c.key} className="text-center px-3">
                          <input
                            type="checkbox"
                            checked={!!row[c.key]}
                            onChange={() => toggle(row.category, c.key)}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={save}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                <Save size={16} /> {saving ? 'Saving…' : 'Save preferences'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationSettings;
